import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Plus, Edit, Trash2, Download } from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { createTraining, updateTraining, deleteTraining } from '../../utils/api';

export const TrainingsManager = ({ trainings, onUpdate, onExport }) => {
  const { toast } = useToast();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', category: '', duration: '', schedule: '', fee: '', syllabus: []
  });

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({ title: '', description: '', category: '', duration: '', schedule: '', fee: '', syllabus: [] });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({ title: item.title || '', description: item.description || '', category: item.category || '', duration: item.duration || '', schedule: item.schedule || '', fee: item.fee || '', syllabus: item.syllabus || [] });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const syllabusArray = formData.syllabus.length > 0 && typeof formData.syllabus[0] === 'string' 
        ? formData.syllabus 
        : (formData.syllabusText || '').split('\n').filter(s => s.trim());
      const submitData = { ...formData, syllabus: syllabusArray };
      delete submitData.syllabusText;
      
      if (editingItem) {
        await updateTraining(editingItem.id, submitData);
        toast({ title: 'Success', description: 'Training updated' });
      } else {
        await createTraining(submitData);
        toast({ title: 'Success', description: 'Training created' });
      }
      setDialogOpen(false);
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Operation failed', variant: 'destructive' });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training?')) return;
    try {
      await deleteTraining(id);
      toast({ title: 'Success', description: 'Training deleted' });
      onUpdate();
    } catch (error) {
      toast({ title: 'Error', description: 'Delete failed', variant: 'destructive' });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>Manage Trainings ({trainings.length})</CardTitle>
          <div className="flex gap-2">
            <Button onClick={onExport}><Download className="w-4 h-4 mr-2" />Export CSV</Button>
            <Button onClick={handleAdd}><Plus className="w-4 h-4 mr-2" />Add Training</Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Duration</TableHead>
              <TableHead>Fee</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {trainings.map((training) => (
              <TableRow key={training.id}>
                <TableCell>{training.title}</TableCell>
                <TableCell>{training.category}</TableCell>
                <TableCell>{training.duration}</TableCell>
                <TableCell>₦{training.fee?.toLocaleString()}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => handleEdit(training)}><Edit className="w-4 h-4" /></Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDelete(training.id)}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingItem ? 'Edit Training' : 'Add Training'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div><Label>Title *</Label><Input required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} /></div>
              <div><Label>Description *</Label><Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Category *</Label><Input required value={formData.category} onChange={(e) => setFormData({ ...formData, category: e.target.value })} placeholder="e.g., MANAGEMENT" /></div>
                <div><Label>Duration *</Label><Input required value={formData.duration} onChange={(e) => setFormData({ ...formData, duration: e.target.value })} placeholder="e.g., 4 weeks" /></div>
              </div>
              <div><Label>Schedule *</Label><Input required value={formData.schedule} onChange={(e) => setFormData({ ...formData, schedule: e.target.value })} placeholder="e.g., Weekends 9AM-5PM" /></div>
              <div><Label>Fee (₦) *</Label><Input required type="number" value={formData.fee} onChange={(e) => setFormData({ ...formData, fee: e.target.value })} /></div>
              <div><Label>Syllabus (one per line)</Label><Textarea rows={6} value={formData.syllabusText || (formData.syllabus || []).join('\n')} onChange={(e) => setFormData({ ...formData, syllabusText: e.target.value })} placeholder="Module 1: Introduction\nModule 2: Advanced Topics" /></div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingItem ? 'Update' : 'Create'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};